import type { Coordinate } from "./types";

const RAD = Math.PI / 180;
const EARTH = 6371008.8;
export const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
export const angleDifference = (to: number, from: number) => (((to - from) % 360 + 540) % 360) - 180;

export function distance(a: Coordinate, b: Coordinate): number {
    const h = Math.sin((b[1] - a[1]) * RAD / 2) ** 2 + Math.cos(a[1] * RAD) * Math.cos(b[1] * RAD) * Math.sin((b[0] - a[0]) * RAD / 2) ** 2;
    return 2 * EARTH * Math.asin(Math.sqrt(clamp(h, 0, 1)));
}

export function bearing(a: Coordinate, b: Coordinate): number {
    const x = Math.sin((b[0] - a[0]) * RAD) * Math.cos(b[1] * RAD);
    const y = Math.cos(a[1] * RAD) * Math.sin(b[1] * RAD) - Math.sin(a[1] * RAD) * Math.cos(b[1] * RAD) * Math.cos((b[0] - a[0]) * RAD);
    return (Math.atan2(x, y) / RAD + 360) % 360;
}

export function destination(origin: Coordinate, heading: number, meters: number): Coordinate {
    const a = meters / EARTH, course = heading * RAD, lat = origin[1] * RAD, lng = origin[0] * RAD;
    const latitude = Math.asin(Math.sin(lat) * Math.cos(a) + Math.cos(lat) * Math.sin(a) * Math.cos(course));
    const longitude = lng + Math.atan2(Math.sin(course) * Math.sin(a) * Math.cos(lat), Math.cos(a) - Math.sin(lat) * Math.sin(latitude));
    return [longitude / RAD, latitude / RAD];
}

export function linePoint(coordinates: Coordinate[], fraction: number): Coordinate {
    if (fraction <= 0) return [...coordinates[0]];
    if (fraction >= 1) return [...coordinates[coordinates.length - 1]];
    const lengths = coordinates.slice(1).map((point, index) => distance(coordinates[index], point));
    let remaining = lengths.reduce((sum, length) => sum + length, 0) * fraction;
    for (let index = 0; index < lengths.length; index++) {
        if (remaining <= lengths[index] && lengths[index] > 0) {
            const ratio = remaining / lengths[index];
            return [coordinates[index][0] + (coordinates[index + 1][0] - coordinates[index][0]) * ratio,
                coordinates[index][1] + (coordinates[index + 1][1] - coordinates[index][1]) * ratio];
        }
        remaining -= lengths[index];
    }
    return [...coordinates[coordinates.length - 1]];
}

export function lineSlice(coordinates: Coordinate[], from: number, to: number): Coordinate[] {
    const length = coordinates.slice(1).reduce((sum, point, index) => sum + distance(coordinates[index], point), 0);
    const result = [linePoint(coordinates, from)];
    let progress = 0;
    for (let index = 1; index < coordinates.length; index++) {
        progress += distance(coordinates[index - 1], coordinates[index]);
        const fraction = progress / Math.max(length, 0.001);
        if (fraction > from && fraction < to) result.push(coordinates[index]);
    }
    result.push(linePoint(coordinates, to));
    return result;
}
